import { isTableOpsOnlyCapabilities } from './isTableOpsOnlyCapabilities';
import { isTableOpsRoleScopedUser } from './isTableOpsRoleScopedUser';
import { isVisitorUser } from './isVisitorUser';

type NavItem = { path?: string | null };
type Capabilities = Record<string, boolean> | null | undefined;

export type LandingPolicyInput = {
  user: any;
  capabilities: Capabilities;
  nav: NavItem[] | null | undefined;
};

const TABLEOPS_PATH = '/tableops';

const DESKTOP_ROOT_PATHS = ['/developer', '/admin', '/system', '/devices', '/ttlock'];

function getNavPaths(nav: NavItem[] | null | undefined): string[] {
  return Array.isArray(nav) ? nav.map((item) => String(item?.path || '')).filter(Boolean) : [];
}

function hasDeveloperSurface(capabilities: Capabilities, navPaths: string[]): boolean {
  if (capabilities?.isSuperadmin === true) return true;
  if (capabilities?.canViewDeveloper === true) return true;
  return navPaths.some((path) => path === '/developer' || path.startsWith('/developer?'));
}

export function canAccessDeveloperSurface(args: LandingPolicyInput): boolean {
  const navPaths = getNavPaths(args.nav);
  return hasDeveloperSurface(args.capabilities, navPaths);
}

/**
 * Canonical TableOps-first landing policy used across root/login/layout guards.
 */
export function shouldForceTableOpsLanding(args: LandingPolicyInput): boolean {
  const { user, capabilities, nav } = args;
  if (!user) return false;

  if (capabilities?.isTableOpsOperator === true) return true;

  const navPaths = getNavPaths(nav);
  const hasTableopsNav = navPaths.some((path) =>
    ['/tableops', '/cash-register', '/order', '/stock', '/sales-purchase'].includes(path),
  );
  const hasDesktopNav = navPaths.some((path) =>
    ['/developer', '/admin', '/ttlock', '/devices', '/system'].includes(path),
  );

  return (
    isTableOpsRoleScopedUser(user) ||
    isTableOpsOnlyCapabilities(capabilities ?? {}) ||
    (hasTableopsNav && !hasDesktopNav)
  );
}

export function resolveAuthenticatedHome(args: LandingPolicyInput): string {
  const { user, capabilities, nav } = args;
  if (!user || isVisitorUser(user)) return '/';

  if (shouldForceTableOpsLanding(args)) return TABLEOPS_PATH;

  const navPaths = getNavPaths(nav);
  if (hasDeveloperSurface(capabilities, navPaths)) return '/developer';

  const firstNavPath = navPaths.find((path) => path.startsWith('/'));
  return firstNavPath || '/';
}

export function normalizePostLoginTarget(rawTarget: string, args: LandingPolicyInput): string {
  const fallback = resolveAuthenticatedHome(args);
  if (!rawTarget || rawTarget === '/') return fallback;

  if (shouldForceTableOpsLanding(args)) {
    if (DESKTOP_ROOT_PATHS.some((p) => rawTarget.startsWith(p))) {
      return TABLEOPS_PATH;
    }
    return rawTarget;
  }

  if (!canAccessDeveloperSurface(args) && rawTarget.startsWith('/developer')) {
    return fallback;
  }

  return rawTarget;
}
